import { Icon } from '@app/components'
import { Brand, ThemeToggle } from '@app/components/site'
import { useAuth } from '@app/lib/auth'
import { useRouteFocus } from '@app/lib/hooks'
import { type ReactNode, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'

export interface NavItem {
  to: string
  label: string
  end?: boolean
}
export interface NavGroup {
  label?: string
  items: NavItem[]
}

export function AppShellLayout({
  title,
  groups,
  extra,
}: {
  title: string
  groups: NavGroup[]
  extra?: ReactNode
}) {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const main = useRef<HTMLElement>(null)
  useRouteFocus(main)
  const initials = user
    ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase() ||
      user.email[0]?.toUpperCase()
    : '?'
  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className="app-sidebar" aria-label="Sidebar">
        <div className="app-sidebar__top">
          <Link to="/" className="brand-link" aria-label={`BTown Pride home (${title})`}>
            <Brand size="small" name={title} showName />
          </Link>
          <button
            type="button"
            className="btn btn--ghost btn--icon app-nav__toggle"
            aria-expanded={open}
            aria-controls="app-nav"
            aria-label={open ? 'Close navigation' : 'Open navigation'}
            onClick={() => setOpen((o) => !o)}
          >
            <Icon name={open ? 'x' : 'menu'} />
          </button>
        </div>
        <nav
          id="app-nav"
          className={`app-nav${open ? ' is-open' : ''}`}
          aria-label={`${title} navigation`}
        >
          {extra ? <div className="app-nav__extra">{extra}</div> : null}
          {groups.map((g) => (
            <div key={g.label ?? 'main'}>
              {g.label ? <div className="app-nav__group">{g.label}</div> : null}
              {g.items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.end} onClick={() => setOpen(false)}>
                  {i.label}
                </NavLink>
              ))}
            </div>
          ))}
          <div style={{ padding: '0 var(--bp-space-2) var(--bp-space-2)' }}>
            <ThemeToggle compact />
          </div>
          <div className="app-user">
            <div className="avatar" aria-hidden="true">
              {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="truncate bold">{user?.displayName}</div>
              <div className="truncate tiny muted">{user?.email}</div>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        </nav>
      </aside>
      <main
        id="main"
        className="app-main"
        key={location.pathname.split('/').slice(0, 3).join('/')}
        ref={main}
        tabIndex={-1}
      >
        <Outlet />
      </main>
    </div>
  )
}
